
import { vueSet } from 'vue-deepset';
import { sync } from 'vuex-pathify';

import * as sharedUtil from '@/../universal/util';

import ConfirmDialog from 'ui-kit/src/components/ConfirmDialog';

function syncWithClone(whats) {
    return _.mapValues(_.keyBy(whats), (what) => ({
        get() { return this.$store.get(what); },
        set(value) { this.$store.set(what, _.cloneDeep(value)); },
    }));
}

export default {
    constants: {
        dateFormat: 'D. M. YYYY',
        timeFormat: 'H:mm',
    },

    computed: {
        ...sync([
            'branches',
            'cases',
            'claims',
            'clients',
            'enums',
            'insurers',
            'intervalSets',
            'persons',
            'settings',
            'users',
            'vehicleCategories',
            'vehicles',

            'lastFetch',

            'currentUser',
            'loggedIn',
        ]),
        ...syncWithClone([
        ]),
        dateTimeFormat() {
            return `${this.dateFormat} ${this.timeFormat}`;
        },
        canDestroy() {
            // Only the 1st system user can destroy items
            return this.getSetting('allowDestroy') && this.currentUser?.id === 1;
        },
    },

    methods: {
        ...sharedUtil,
        loc(obj) {
            return !obj ? null : obj[this.$i18n.locale] || obj.cs || obj.en || null;
        },
        tomorrow() {
            return dayjs().add(1, 'days').startOf('day').format();
        },
        formatDate(date) {
            return !date ? null : dayjs(date).format(this.dateFormat);
        },
        formatDateTime(date) {
            return !date ? null : dayjs(date).format(this.dateTimeFormat);
        },
        formatDateRange(from, to) {
            if (!from || !to)
                return this.formatDate(from || to);

            const fd = dayjs(from);
            const td = dayjs(to);
            const format = this.dateFormat;

            if (fd.isSame(td, 'day'))
                return fd.format(format);

            if (fd.isSame(td, 'month'))
                return `${fd.format(format.replace(/ *M.*/, ''))} – ${td.format(format)}`;

            if (fd.isSame(td, 'year'))
                return `${fd.format(format.replace(/ *Y.*/, ''))} – ${td.format(format)}`;

            return `${fd.format(format)} – ${td.format(format)}`;
        },
        formatDateTimeRange(from, to) {
            if (!from || !to)
                return this.formatDateTime(from || to);

            const fd = dayjs(from);
            const td = dayjs(to);
            const format = this.dateTimeFormat;

            if (fd.isSame(td))
                return fd.format(format);

            if (fd.isSame(td, 'day'))
                return `${fd.format(format)} – ${td.format(this.timeFormat)}`;

            return `${fd.format(format)} – ${td.format(format)}`;
        },
        formatPhone(phone) {
            if (phone && !phone.startsWith('+') && !phone.startsWith('00')) {
                const triplets = phone.replace(/[\D]/g, '').match(/.{1,3}/g);

                return !triplets ? null : `+420 ${triplets.join(' ')}`;
            } else {
                return phone || null;
            }
        },

        deCamelCase(str) {
            return str.replace(/([a-z])([A-Z])/g, (_, p1, p2) => `${p1} ${p2.toLowerCase()}`);
        },
        hyphenate(str) {
            return str.replace(/[^A-Za-z0-9]+/g, '-');
        },
        filenamify(value) {
            return this.hyphenate(this.removeAccents(`${value}`.toLowerCase()));
        },

        normalize(text) {
            return this.removeAccents((text || '').toLowerCase());
        },
        // Note: Takes string or array of strings as 1st argument
        normalizedIncludes(haystacks, needle) {
            if (!Array.isArray(haystacks)) {
                haystacks = [haystacks];
            }

            return haystacks.some(h => this.normalize(h).includes(this.normalize(needle)));
        },
        normalizedEquals(text1, text2) {
            return this.normalize(text1) === this.normalize(text2);
        },
        csvFieldPathsToHaystacks(item, filter) {
            return filter.split(',').map(ps => ps.split(' ').map(p => this.get(item, p)).filter(v => v).join(' '));
        },
        filterByCsvFieldPaths(item, csvFieldPaths, text) {
            return !text || this.normalizedIncludes(this.csvFieldPathsToHaystacks(item, csvFieldPaths), text);
        },

        // Note: Works only for simple field values (strings, numbers) for now
        isEmpty(value) {
            return value == null || value === '';
        },
        idEquals(a, b) {
            return (a && a.id) === (b && b.id);
        },
        get(obj, path) {
            return _.get(obj, path);
        },
        // Note: Unlike lodash's set, retains reactivity when adding a new property
        set(obj, path, value) {
            vueSet(obj, path, value);

            return obj;
        },
        setIfEmpty(obj, path, value) {
            if (this.isEmpty(this.get(obj, path))) {
                this.set(obj, path, value);
            }
        },
        removeOne(array, value) {
            const idx = array.indexOf(value);

            if (idx !== -1) {
                array.splice(idx, 1);
            }
        },
        replaceOne(array, value, replacement) {
            const idx = array.indexOf(value);

            if (idx !== -1) {
                array.splice(idx, 1, replacement);
            }
        },
        t(path, returnPathIfNotFound = true) {
            const val = this.$t(path);

            return val?.default || val || (returnPathIfNotFound ? path : undefined);
        },
        tv(path, variant, returnPathIfNotFound = true) {
            const val = this.$t(path);

            return val
                ? val[variant] || val.default || val
                : (returnPathIfNotFound ? path : undefined);
        },
        ttl(term, casus) {
            return this.tv(`terms.${term}.${casus}`, 'long');
        },
        tts(term, casus) {
            return this.tv(`terms.${term}.${casus}`, 'short');
        },
        getFieldLabel(collName, fieldPath, variants) {
            let val = this.$t(`fields.${collName}.${fieldPath}`);
            if (val) return this.mapFind(variants, v => val[v]) || val.default || val;

            const fieldName = fieldPath.substr(fieldPath.lastIndexOf('.') + 1);

            val = this.$t(`fields.common.${fieldName}`);
            if (val) return this.mapFind(variants, v => val[v]) || val.default || val;

            return fieldPath;
        },
        getFieldUnit(collName, fieldPath, count) {
            let val = this.$tc(`fields.${collName}.${fieldPath}.unit`, count);
            if (val) return val;

            const fieldName = fieldPath.substr(fieldPath.lastIndexOf('.') + 1);

            val = this.$tc(`fields.common.${fieldName}.unit`, count);
            if (val) return val;

            return undefined;
        },
        getNu(collName, subType) {
            const path = subType ? `${collName}.subTypes.${subType}` : collName;

            return _.cloneDeep(_.get(types, path).nu);
        },
        getSelectOptions(collName, fieldPath) {
            const labels = this.$t(`fields.${collName}.${fieldPath}.options`);

            return _.get(types, `${collName}.fields.${fieldPath}.options`).map(o => ({
                value: o,
                label: labels[o].default || labels[o],
            }));
        },
        getSelectOptionLabel(collName, fieldPath, value) {
            const val = this.$t(`fields.${collName}.${fieldPath}.options.${value}`);

            return val.default || val;
        },
        getExpandedRefDefs(baseCollName) {
            return _.mapValues(types[baseCollName].refs, (v, refName) => this.getExpandedRefDef(baseCollName, refName, v));
        },
        getExpandedRefDef(baseCollName, refName, rawRefDef = null) {
            // Get raw ref def (may be provided as last argument as an optimization)
            rawRefDef = rawRefDef || types[baseCollName].refs[refName];

            const { collName, fieldPath, recipe, isEmbedded, refFields, subItemsPrefix } = rawRefDef;

            return {
                collName: typeof rawRefDef === 'string' ? rawRefDef : collName,
                fieldPath: fieldPath || refName.substr(0, refName.indexOf('Ref')),
                recipe: recipe || ((ref, items) => this.findById(items, ref)),
                isEmbedded: isEmbedded != null ? isEmbedded : false,
                refFields,
                subItemsPrefix,
            };
        },
        actualizeRefOnDemand(baseCollName, items, refName) {
            const { collName, fieldPath, recipe, subItemsPrefix } = this.getExpandedRefDef(baseCollName, refName);

            items = !items ? this[baseCollName] : items.length ? items : [items];

            for (const item of items) {
                this.actualizeRef_(item, refName, collName, fieldPath, recipe, subItemsPrefix);
            }
        },
        actualizeRefs(refDefs, item) {
            _.forEach(refDefs, ({ collName, fieldPath, recipe, subItemsPrefix }, refName) => {
                this.actualizeRef_(item, refName, collName, fieldPath, recipe, subItemsPrefix);
            });
        },
        actualizeRef_(item, refName, refCollName, fieldPath, recipe, subItemsPrefix) {
            if (this.get(item, fieldPath)) {
                // Do nothing if already actualized
                return;
            } else if (subItemsPrefix) {
                // Recursively process refs in sub-items
                const idx = subItemsPrefix.length;
                const inFieldPath = fieldPath.substr(idx + 1);
                const inRefName = refName.substr(idx + 1);
                const subItems = this.get(item, subItemsPrefix) || [];

                for (const i of subItems) {
                    this.actualizeRef_(i, inRefName, refCollName, inFieldPath, recipe);
                }
            } else {
                // Actualize single ref
                const ref = this.get(item, refName);

                if (ref) {
                    this.set(item, fieldPath, recipe(ref, this[refCollName]));
                }
            }
        },
        // Actualize refs and fetch all necessary data for one or more items
        async actualizeAndFetch(collName, items, fetchRefsOnly = false, forDetail = false) {
            // Get fully expanded ref definitions
            const refDefs = this.getExpandedRefDefs(collName);

            // Gather collections to be fetched
            const collNames = _.uniq([
                // Main collection, settings
                ...(fetchRefsOnly ? [] : ['settings', collName]),
                // Ref collections
                ..._.map(refDefs, v => v.collName),
                // Extra collections (covers reverse refs, indirect refs etc.)
                ...(types[collName].extraFetch?.[forDetail ? 'detail' : 'master'] ?? []),
            ]);

            // Fetch items
            await this.fetchAll(...collNames);

            // Gather items whose refs are to be actualized
            items = !items ? this[collName] : items.length ? items : [items];

            // Actualize refs
            for (const item of items) {
                this.actualizeRefs(refDefs, item);
            }

            // Get page collection name
            const pageCollName = _.findKey(types, v => v.pageName === this.$route.name);

            // If page collection items were fetched from within detail, refresh page master data
            if (pageCollName !== collName && collNames.includes(pageCollName)) {
                await this.actualizeAndFetch(pageCollName);
            }
        },

        goToMaster(name = this.$route.name) {
            this.$router.push({ name });
        },
        goToDetail(name = this.$route.name, id = 0) {
            this.$router.push({ name, params: { id } });
        },
        getDetail(name = this.$route.name, id = 0, query) {
            return {
                name,
                params: { id },
                query,
            };
        },

        async logIn(email, password) {
            const response = await axios.post('/logIn', {
                email,
                password,
            }, { withCredentials: true });

            const authToken = response.data;
            const path = sessionStorage.getItem('redirectPath') || '/';

            axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;

            localStorage.setItem('authToken', authToken);
            sessionStorage.removeItem('redirectPath');

            this.$store.set('resetState');
            this.loggedIn = true;
            this.currentUser = await this.fetchOne('users', 'self');

            this.$router.push(path);
        },
        async logOut() {
            try {
                await axios.post('/logOut', {}, { withCredentials: true });
            } catch (err) {
                debug.error('API logout failed:', err);
            }

            delete axios.defaults.headers.common['Authorization'];

            localStorage.removeItem('authToken');
            sessionStorage.setItem('redirectPath', this.$route.fullPath);

            if (this.$route.meta.isPublic) {
                window.location.reload();
            } else {
                this.$store.set('resetState');
                this.$router.push({ name: 'login' });
            }
        },

        fetchAll(...whats) {
            // return Promise.all(whats.map(this.fetchAllFull));
            // TODO: Handle destroyed items (or switch back to fetchAllFull)
            return Promise.all(whats.map(this.fetchAllUpdated));
        },
        async fetchAllFull(what) {
            const { data } = await axios(`/${what}`);

            // debug.info(`Fetched ${data.length} ${this.deCamelCase(what)}.`);

            this[what] = data;
        },
        async fetchAllUpdated(what) {
            const since = this.lastFetch[what];
            const dateStr = since ? dayjs(since).format(dayjs(since).isSame(dayjs(), 'day') ? 'H:mm:ss' : 'D/M/YYYY H:mm:ss') : null;
            const sinceStr = since ? ` updated since ${dateStr}` : '';
            const now = Date.now();
            const { data } = await axios(`/${what}`, { params: since ? { since } : null });

            // debug.info(`Fetched ${data.length} ${this.deCamelCase(what)}${sinceStr}.`);

            if (!this[what]) {
                this[what] = data;
            } else {
                for (const item of data) {
                    const idx = this[what].findIndex(i => i.id === item.id);

                    if (idx !== -1) {
                        this.updateIn(this[what], item);
                    } else {
                        this[what].push(item);
                    }
                }
            }

            this.lastFetch[what] = now;
        },
        async fetchOne(what, id, actualizeRefs = false) {
            const item = await this.apiGetOne(what, id);

            if (actualizeRefs) {
                await this.actualizeAndFetch(what, item, true, true);
            }

            if (this[what]) {
                const idx = this[what].findIndex(i => i.id === item.id);

                if (idx !== -1) {
                    this.updateIn(this[what], item);
                } else {
                    this[what].push(item);
                }
            }

            return item;
        },
        async apiGetOne(what, id) {
            const response = await axios.get(`/${what}/${id}`);

            return response.data;
        },
        findIn(items, itemOrId) {
            return this.findById(items, itemOrId.id ?? itemOrId);
        },

        async createOne(what, item, actualizeRefs = false) {
            const result = await this.apiCreateOne(what, item);

            if (actualizeRefs) {
                await this.actualizeAndFetch(what, result, true, true);
            }

            if (this[what]) {
                // Avoid duplicate in case coll was fetched during the above await
                const idx = this[what].findIndex(i => i.id === result.id);

                if (idx === -1) {
                    this[what].push(result);
                }
            }

            return result;
        },
        async apiCreateOne(what, item) {
            const response = await axios.post(`/${what}`, item);

            return response.data;
        },

        async updateOne(what, item, actualizeRefs = false) {
            const result = await this.apiUpdateOne(what, item);

            if (actualizeRefs) {
                await this.actualizeAndFetch(what, result, true, true);
            }

            if (this[what]) {
                this.updateIn(this[what], result);
            }

            return result;
        },
        async apiUpdateOne(what, item) {
            const response = await axios.put(`/${what}/${item.id}`, item);

            return response.data;
        },
        async apiUpdateMany(what, items) {
            const response = await axios.put(`/${what}`, items);

            return response.data;
        },
        updateIn(items, item) {
            const idx = items.findIndex(i => i.id === item.id);

            debug.assert(idx !== -1, () => `Item with ID ${item.id} not found in ${items}.`);

            items.splice(idx, 1, item);

            return items;
        },

        async safeArchiveOne(what, item, actualizeRefs = false) {
            return new Promise(resolve => {
                this.$q.dialog({
                    component: ConfirmDialog,
                    title: this.$t(`confirm.archive.title.${what}`),
                    message: this.$t(`confirm.archive.message.${what}`),
                    okLabel: 'Smazat',
                    cancelLabel: 'Storno',
                }).onOk(async () => {
                    resolve(await this.archiveOne(what, item, actualizeRefs));
                }).onCancel(() => {
                    resolve(null);
                });
            });
        },
        async archiveOne(what, item, actualizeRefs = false) {
            return this.updateOne(what, {
                id: item.id,
                archived: Date.now(),
            }, actualizeRefs);
        },

        async safeRestoreOne(what, item, actualizeRefs = false) {
            return new Promise(resolve => {
                this.$q.dialog({
                    component: ConfirmDialog,
                    title: this.$t(`confirm.restore.title.${what}`),
                    message: this.$t(`confirm.restore.message.${what}`),
                    okLabel: 'Obnovit',
                    cancelLabel: 'Zrušit',
                }).onOk(async () => {
                    resolve(await this.restoreOne(what, item, actualizeRefs));
                }).onCancel(() => {
                    resolve(null);
                });
            });
        },
        async restoreOne(what, item, actualizeRefs = false) {
            return this.updateOne(what, {
                id: item.id,
                archived: null,
            }, actualizeRefs);
        },

        async safeDestroyOne(what, item) {
            const isReservation = (what === 'cases' && (item.isInternal || !item.contract.signed));

            let title, message;

            if (isReservation) {
                title = `Zrušit${item.isInternal ? ' interní' : ''} rezervaci?`;
                message = `Data o${item.isInternal ? ' interní' : ''} rezervaci budou smazána a vozidlo bude na daný termín uvolněno.`;
            } else {
                title = this.$t(`confirm.archive.title.${what}`).replace('Smazat', 'Trvale smazat');
                message = "Data budou trvale odstraněna ze systému, bez možnosti obnovy.";
            }

            return new Promise(resolve => {
                this.$q.dialog({
                    component: ConfirmDialog,
                    title,
                    message,
                    okLabel: isReservation ? 'Zrušit' : 'Smazat',
                    cancelLabel: 'Storno',
                }).onOk(async () => {
                    await this.destroyOne(what, item.id);

                    resolve(true);
                }).onCancel(() => {
                    resolve(false);
                });
            });
        },
        async destroyOne(what, id) {
            const result = await this.apiDestroyOne(what, id);

            if (this[what]) {
                this.deleteIn(this[what], id);
            }

            return result;
        },
        async apiDestroyOne(what, id) {
            const response = await axios.delete(`/${what}/${id}`);

            return response.data;
        },
        deleteIn(items, itemOrId) {
            const id = itemOrId.id ?? itemOrId;
            const idx = items.findIndex(i => i.id === id);

            debug.assert(idx !== -1, () => `Item with ID ${id} not found in ${items}.`);

            items.splice(idx, 1);

            return items;
        },

        async downloadFile({ name }) {
            const { data } = await axios.get('/download', {
                params: { name },
                responseType: 'blob',
            });

            this.downloadBlob(data, this.getOriginalFilename(name));
        },
        downloadBlob(data, filename) {
            const url = window.URL.createObjectURL(new Blob([data]));
            const link = document.createElement('a');

            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },

        nextId(items) {
            const max = _.maxBy(items, 'id');
            const id = max ? max.id + 1 : 1;

            return id;
        },

        isImmutable(collName, item) {
            return collName === 'users' && item?.isSystemUser;
        },

        getSetting(key) {
            return this.get(this.settings?.find(s => s.key === key), 'value');
        },
        getOriginalFilename(filename) {
            return filename.substr(filename.indexOf('___') + 3);
        },
        getFileTimestamp(filename) {
            return parseInt(filename.substr(0, filename.indexOf('___')));
        },
        getPriceColumns(intervalSet) {
            if (!intervalSet)
                return [];

            let columns = [];

            for (let i = 1; i < intervalSet.values.length; ++i) {
                const a = intervalSet.values[i - 1] + 1;
                const b = intervalSet.values[i];
                const interval = (a === b) ? a : `${a}-${b}`;

                columns.push({
                    name: i - 1,
                    label: `${interval} ${this.getFieldUnit('vehicleCategories', 'dailyPrices.intervals', b)}`,
                    field: r => r[i - 1],
                    align: 'left',
                });
            }

            return columns;
        },

        valuesToIntervals(values) {
            let intervals = [];

            for (let i = 1; i < values.length; ++i) {
                intervals.push(`${values[i - 1] + 1}-${values[i]}`);
            }

            return intervals;
        },
        valuesToFormattedIntervals(values) {
            let intervals = [];

            for (let i = 1; i < values.length; ++i) {
                const a = values[i - 1] + 1;
                const b = values[i];

                intervals.push(`${a === b ? `${a}` : `${a}-${b}`} ${this.$tc('days', b)}`);
            }

            return intervals;
        },

        async prefillFromAres(editItem) {
            debug.info("Prefilling from ARES...");

            const { data } = await axios.get(`/ares?ico=${editItem.ico}`);

            if (!data) {
                notifyInfo('IČO nebylo nalezeno.');
                return;
            }

            const { name, street, city, zip } = data;

            if (editItem.personRef != null) {
                [editItem.person.firstName, editItem.person.lastName] = name.split(' ');

                editItem.person.street = street;
                editItem.person.city = city;
                editItem.person.zip = zip;
            } else {
                editItem.companyName = name;
                editItem.street = street;
                editItem.city = city;
                editItem.zip = zip;
            }

            debug.info("Prefill from ARES complete.");
        },
    },
};
